import { useEffect, useState } from "react";
import {
  Plus, X, Check, Loader2, ScrollText, Download, BellRing, ListChecks, Copy,
} from "lucide-react";
import { downloadFromUrl } from "@/lib/download";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function DigestHistoryRow({ row, onChange }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(row.comment || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(row.comment || ""); }, [row.comment]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/outbound/digest-history/${row.history_id}/comment`, { comment: text });
      onChange?.({ ...row, comment: data.comment });
      setEditing(false);
      toast.success("Comment saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };
  const cancel = () => { setText(row.comment || ""); setEditing(false); };

  if (editing) {
    return (
      <div className="mt-1.5 flex items-start gap-1.5" data-testid={`ai-outbound-history-comment-edit-${row.history_id}`}>
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Add a note (max 1000 chars)…"
                  className="text-xs" />
        <div className="flex flex-col gap-1">
          <Button size="sm" onClick={save} disabled={saving}
                  data-testid={`ai-outbound-history-comment-save-${row.history_id}`}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          </Button>
          <Button size="sm" variant="outline" onClick={cancel}
                  data-testid={`ai-outbound-history-comment-cancel-${row.history_id}`}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }
  if (row.comment) {
    return (
      <button type="button" onClick={() => setEditing(true)}
              data-testid={`ai-outbound-history-comment-view-${row.history_id}`}
              className="mt-1 block max-w-full text-left text-[11px] italic text-slate-500 hover:text-primary hover:underline"
              title="Click to edit note">
        &ldquo;{row.comment}&rdquo;
      </button>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)}
            data-testid={`ai-outbound-history-comment-add-${row.history_id}`}
            className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400 hover:text-primary">
      <Plus className="h-3 w-3" /> Add note
    </button>
  );
}

function ResendDigestDialog({ open, onClose, row }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);
  const send = async () => {
    const recipients = text.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s && s.includes("@"));
    if (recipients.length === 0) { toast.error("Enter at least one valid email"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/outbound/digest-history/${row.history_id}/resend`, { recipients });
      toast.success(`Sent to ${data.recipient_count} recipient${data.recipient_count === 1 ? "" : "s"}.`);
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="ai-outbound-history-resend-dialog">
        <DialogHeader>
          <DialogTitle>Re-send digest</DialogTitle>
          <DialogDescription>{row?.subject}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-slate-600">Recipient emails (comma / space / newline separated)</Label>
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
                    data-testid="ai-outbound-history-resend-emails"
                    placeholder="sales-manager@example.com, ops@example.com" />
          <p className="text-[11px] text-slate-400">Your own email is not auto-included — you already have the archived copy.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-history-resend-cancel">Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="ai-outbound-history-resend-send">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DigestHistoryPanel() {
  const [rows, setRows] = useState(null);
  const [retention, setRetention] = useState(12);
  const [resendRow, setResendRow] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/outbound/digest-history");
      setRows(data.history || []);
      setRetention(data.retention || 12);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);
  const download = (h) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    downloadFromUrl(`${base}/api/outbound/digest-history/${h.history_id}/pdf`, h.filename || "digest.pdf");
  };

  if (rows === null) {
    return (
      <Card className="border-slate-200 p-6 text-center text-xs text-slate-400" data-testid="ai-outbound-history-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 p-4" data-testid="ai-outbound-digest-history">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Digest history</h3>
        </div>
        <span className="text-[11px] text-slate-400">Last {retention} shown</span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400"
             data-testid="ai-outbound-history-empty">
          No digests sent yet. Once a scheduled or manual send fires, its PDF will appear here.
        </div>
      ) : (        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-xs" data-testid="ai-outbound-history-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-1.5 text-left">Sent</th>
                <th className="px-3 py-1.5 text-left">Subject</th>
                <th className="px-3 py-1.5 text-right">Scored</th>
                <th className="px-3 py-1.5 text-right">Hot</th>
                <th className="px-3 py-1.5 text-right">Recipients</th>
                <th className="px-3 py-1.5 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.history_id} data-testid={`ai-outbound-history-row-${r.history_id}`}>
                  <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">
                    {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                    {r.is_manual && <span className="ml-1 text-[10px] font-semibold uppercase text-slate-400">manual</span>}
                  </td>
                  <td className="px-3 py-1.5 text-slate-700">
                    {r.subject}
                    <DigestHistoryRow row={r} onChange={(patched) =>
                      setRows((cur) => cur.map((x) => x.history_id === patched.history_id ? patched : x))
                    } />
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{(r.totals || {}).scored ?? 0}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600 font-medium">{(r.totals || {}).hot ?? 0}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.recipient_count ?? 0}</td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline"
                              onClick={() => download(r)}
                              data-testid={`ai-outbound-history-download-${r.history_id}`}>
                        <Download className="h-3 w-3" /> PDF
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => setResendRow(r)}
                              data-testid={`ai-outbound-history-resend-${r.history_id}`}>
                        <BellRing className="h-3 w-3" /> Resend
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ResendDigestDialog open={!!resendRow} onClose={() => setResendRow(null)} row={resendRow || {}} />
    </Card>
  );
}

function DigestPreviewDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(""); setData(null);
    (async () => {
      try {
        const { data } = await api.get("/outbound/digest-config/preview");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl" data-testid="ai-outbound-digest-preview-dialog">
        <DialogHeader>
          <DialogTitle>Digest preview</DialogTitle>
          <DialogDescription>
            {data ? <span data-testid="ai-outbound-digest-preview-subject">{data.subject}</span> : "Live preview — nothing will be sent."}
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="text-center py-8 text-xs text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin" /> Rendering…</div>}
        {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">{error}</div>}
        {data && (
          <div className="rounded-md border border-slate-200 overflow-hidden bg-white">
            <iframe title="Digest preview" srcDoc={data.html}
                    className="w-full"
                    style={{ height: "600px", border: 0 }}
                    data-testid="ai-outbound-digest-preview-iframe"
                    sandbox="" />
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex items-center gap-2">
            {data && (
              <>
                <Button variant="outline" size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(data.html);
                      toast.success("HTML copied to clipboard");
                    } catch {
                      toast.error("Clipboard blocked by browser");
                    }
                  }}
                  data-testid="ai-outbound-digest-preview-copy-html">
                  <Copy className="h-3.5 w-3.5" /> Copy HTML
                </Button>
                <Button variant="outline" size="sm"
                  onClick={() => {
                    const base = process.env.REACT_APP_BACKEND_URL || "";
                    downloadFromUrl(`${base}/api/outbound/digest-config/preview/pdf`,
                                    `uc-ai-outbound-digest-${new Date().toISOString().slice(0,10)}.pdf`);
                  }}
                  data-testid="ai-outbound-digest-preview-pdf">
                  <Download className="h-3.5 w-3.5" /> Download PDF
                </Button>
              </>
            )}
          </div>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-digest-preview-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DigestConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/outbound/digest-config");
        if (!cancelled) setCfg(data);
      } catch {
        if (!cancelled) setCfg({ enabled: false, frequency: "weekly", time_of_day: "09:00",
                                 timezone: "America/Edmonton", recipients: [] });
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/outbound/digest-config", {
        enabled: cfg.enabled,
        frequency: cfg.frequency,
        time_of_day: cfg.time_of_day,
        timezone: cfg.timezone,
        recipients: cfg.recipients,
        include_org_users: cfg.include_org_users,
        sections: cfg.sections,
        slack_webhook_url: cfg.slack_webhook_url || "",
        teams_webhook_url: cfg.teams_webhook_url || "",
        instant_alerts: cfg.instant_alerts || { enabled: false, on_hot_lead: true, on_negative: false, max_per_hour: 10 },
        attach_pdf: !!cfg.attach_pdf,
      });
      setCfg(data);
      toast.success("Digest settings saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/outbound/digest-config/send-now");
      toast.success("Digest email sent.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };
  const addRecipient = () => {
    const r = (newRecipient || "").trim();
    if (!r || !r.includes("@")) { toast.error("Enter a valid email"); return; }
    if ((cfg.recipients || []).includes(r)) { toast.error("Already added"); return; }
    setCfg({ ...cfg, recipients: [...(cfg.recipients || []), r] });
    setNewRecipient("");
  };
  const removeRecipient = (r) => setCfg({ ...cfg, recipients: (cfg.recipients || []).filter((x) => x !== r) });

  if (loading || !cfg) {
    return (
      <Card className="border-slate-200 p-6 text-center text-xs text-slate-400" data-testid="ai-outbound-digest-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 p-4" data-testid="ai-outbound-digest-config">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Digest email</h3>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled}
                 onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                 data-testid="ai-outbound-digest-enabled"
                 className="h-3.5 w-3.5 accent-primary" />
          Send digest emails
        </label>
      </div>
      {!cfg.email_provider_configured && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
          Email provider not configured (RESEND_API_KEY missing). Save the config now — sends will start once the key is added to the backend.
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-xs font-medium text-slate-600">Frequency</Label>
          <Select value={cfg.frequency} onValueChange={(v) => setCfg({ ...cfg, frequency: v })}>
            <SelectTrigger data-testid="ai-outbound-digest-frequency" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">Delivery time (local)</Label>
          <Input type="time" value={cfg.time_of_day}
                 onChange={(e) => setCfg({ ...cfg, time_of_day: e.target.value })}
                 data-testid="ai-outbound-digest-time"
                 className="h-9" />
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">Timezone (IANA)</Label>
          <Input value={cfg.timezone}
                 onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
                 data-testid="ai-outbound-digest-timezone"
                 placeholder="America/Edmonton"
                 className="h-9" />
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs font-medium text-slate-600">Additional recipients (your own email is always included)</Label>
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="ai-outbound-digest-recipients">
          {(cfg.recipients || []).map((r) => (
            <span key={r} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
                  data-testid={`ai-outbound-digest-recipient-${r}`}>
              {r}
              <button type="button" onClick={() => removeRecipient(r)}
                className="text-slate-400 hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="mt-1.5 flex gap-1.5">
          <Input value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                 placeholder="sales-manager@example.com"
                 data-testid="ai-outbound-digest-recipient-input"
                 className="h-9 max-w-sm" />
          <Button type="button" variant="outline" size="sm" onClick={addRecipient}
                  data-testid="ai-outbound-digest-recipient-add">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      </div>
      {cfg.last_sent_at && (
        <div className="mt-3 text-[11px] text-slate-400" data-testid="ai-outbound-digest-last-sent">
          Last sent: {new Date(cfg.last_sent_at).toLocaleString()}
        </div>
      )}

      <div className="mt-4 rounded-md border border-slate-200 p-3">
        <div className="mb-2 text-[10px] uppercase tracking-wide text-slate-500">Sections to include</div>
        <div className="flex flex-wrap gap-3" data-testid="ai-outbound-digest-sections">
          {[
            ["kpis", "KPI cards"],
            ["movers", "Movers (WoW shifts)"],
            ["campaigns", "Per-campaign table"],
            ["top_objections", "Top objections"],
            ["hot_leads", "Hot leads"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox"
                     checked={!!(cfg.sections || {})[k]}
                     onChange={(e) => setCfg({ ...cfg, sections: { ...(cfg.sections || {}), [k]: e.target.checked } })}
                     data-testid={`ai-outbound-digest-section-${k}`}
                     className="h-3.5 w-3.5 accent-primary" />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-md border border-slate-200 p-3">
        <div className="mb-2 text-[10px] uppercase tracking-wide text-slate-500">Extra delivery targets</div>
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input type="checkbox" checked={!!cfg.include_org_users}
                 onChange={(e) => setCfg({ ...cfg, include_org_users: e.target.checked })}
                 data-testid="ai-outbound-digest-include-org"
                 className="h-3.5 w-3.5 accent-primary" />
          Also send to every staff / partner user in my organization
        </label>
        <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input type="checkbox" checked={!!cfg.attach_pdf}
                 onChange={(e) => setCfg({ ...cfg, attach_pdf: e.target.checked })}
                 data-testid="ai-outbound-digest-attach-pdf"
                 className="h-3.5 w-3.5 accent-primary" />
          Attach a PDF copy of the digest to the email
        </label>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-medium text-slate-600">Slack incoming-webhook URL</Label>
            <Input value={cfg.slack_webhook_url || ""}
                   onChange={(e) => setCfg({ ...cfg, slack_webhook_url: e.target.value })}
                   data-testid="ai-outbound-digest-slack"
                   placeholder="https://hooks.slack.com/services/…"
                   className="h-9" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600">Teams incoming-webhook URL</Label>
            <Input value={cfg.teams_webhook_url || ""}
                   onChange={(e) => setCfg({ ...cfg, teams_webhook_url: e.target.value })}
                   data-testid="ai-outbound-digest-teams"
                   placeholder="https://outlook.office.com/webhook/…"
                   className="h-9" />
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-md border border-slate-200 p-3">
        <div className="mb-2 flex items-center gap-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Instant alerts</div>
          <span className="text-[10px] text-slate-400">(Fires the moment a call scores — uses the Slack / Teams URLs above)</span>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input type="checkbox"
                 checked={!!((cfg.instant_alerts || {}).enabled)}
                 onChange={(e) => setCfg({ ...cfg, instant_alerts: { ...(cfg.instant_alerts || {}), enabled: e.target.checked } })}
                 data-testid="ai-outbound-alerts-enabled"
                 className="h-3.5 w-3.5 accent-primary" />
          Enable instant alerts
        </label>
        <div className="mt-2 flex flex-wrap items-center gap-3 pl-5">
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox"
                   checked={!!((cfg.instant_alerts || {}).on_hot_lead)}
                   onChange={(e) => setCfg({ ...cfg, instant_alerts: { ...(cfg.instant_alerts || {}), on_hot_lead: e.target.checked } })}
                   data-testid="ai-outbound-alerts-hot-lead"
                   className="h-3.5 w-3.5 accent-primary" />
            Hot lead detected
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox"
                   checked={!!((cfg.instant_alerts || {}).on_negative)}
                   onChange={(e) => setCfg({ ...cfg, instant_alerts: { ...(cfg.instant_alerts || {}), on_negative: e.target.checked } })}
                   data-testid="ai-outbound-alerts-negative"
                   className="h-3.5 w-3.5 accent-primary" />
            Negative sentiment call
          </label>
          <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-700">
            <Label className="text-xs text-slate-600 whitespace-nowrap m-0">Max / hour / campaign</Label>
            <Input type="number" min="0" max="1000"
                   value={(cfg.instant_alerts || {}).max_per_hour ?? 10}
                   onChange={(e) => setCfg({ ...cfg, instant_alerts: { ...(cfg.instant_alerts || {}), max_per_hour: Math.max(0, Number(e.target.value) || 0) } })}
                   data-testid="ai-outbound-alerts-max-per-hour"
                   title="0 = unlimited"
                   className="h-8 w-20" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}
                data-testid="ai-outbound-digest-preview">
          <ListChecks className="h-3.5 w-3.5" /> Preview
        </Button>
        <Button variant="outline" size="sm" onClick={sendNow} disabled={sending}
                data-testid="ai-outbound-digest-send-now">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Send test now
        </Button>
        <Button size="sm" onClick={save} disabled={saving}
                data-testid="ai-outbound-digest-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>
      <DigestPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </Card>
  );
}
